import { useState } from "react";

import CodeEditor from "@/components/CodeEditor";
import { languages } from "@/components/CodeEditor/constants";
import { LanguageType } from "@/components/CodeEditor/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface DeploymentGuideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promptName?: string;
  inputVariables: string[];
  mostRecentLabel?: string | null;
}

const DeploymentGuideDialog = ({
  open,
  onOpenChange,
  promptName,
  inputVariables,
  mostRecentLabel,
}: DeploymentGuideDialogProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageType>(languages[0]);

  // Generate input variables object for code examples
  const generateInputVariablesObject = (language: "python" | "javascript") => {
    if (inputVariables.length === 0) {
      return language === "python"
        ? '{\n        "topic": "The American Revolution"\n    }'
        : '{\n        topic: "The American Revolution"\n    }';
    }

    const getSampleValue = (variable: string): string => {
      if (variable.includes("message")) {
        return "Hello, how can you help me?";
      }

      if (variable.includes("history")) {
        return "Previous conversation context";
      }

      if (variable.includes("progress")) {
        return "Task in progress";
      }

      return `Sample value for ${variable}`;
    };

    const formatVariableForLanguage = (variable: string, sampleValue: string): string => {
      if (language === "python") {
        return `        "${variable}": "${sampleValue}"`;
      }

      return `        ${variable}: "${sampleValue}"`;
    };

    const sampleValues = inputVariables.map(variable => {
      const sampleValue = getSampleValue(variable);
      return formatVariableForLanguage(variable, sampleValue);
    });

    return language === "python"
      ? `{\n${sampleValues.join(",\n")}\n    }`
      : `{\n${sampleValues.join(",\n")}\n    }`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Deploying a Prompt</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="space-y-4">
            <p className="text-gray-600">
              When you deploy a prompt in PromptLayer, you publish the template so your app can pull
              it at runtime—asking for the latest version, a specific version number, or a label
              like "prod" or "staging", and automatically send the request + response back to
              PromptLayer for full tracking. This keeps copy tweaks and experimentation out of code
              releases while giving your team built-in version history, usage analytics, and
              debugging traces.
            </p>
          </div>

          <div className="space-y-4">
            <div className="max-w-full">
              <CodeEditor
                languages={languages}
                selectedLanguage={selectedLanguage}
                handleLanguageChange={setSelectedLanguage}
                controlsType="tabs"
                code={
                  selectedLanguage === "PYTHON"
                    ? `from promptlayer import PromptLayer

# Initialize PromptLayer client
pl_client = PromptLayer(api_key="your_api_key")

# Run the "${promptName || "your-prompt"}" prompt template
response = pl_client.run(
    prompt_name="${promptName || "your-prompt"}",
    input_variables=${generateInputVariablesObject("python")},${
      mostRecentLabel ? `\n    prompt_release_label="${mostRecentLabel}",` : ""
    }
    tags=["production", "tutorial"],
    metadata={"user_id": "abc123"}
)`
                    : `import { PromptLayer } from "promptlayer";

// Initialize PromptLayer client
const pl_client = new PromptLayer({ apiKey: "your_api_key" });

// Run the "${promptName || "your-prompt"}" prompt template
const response = await pl_client.run({
    promptName: "${promptName || "your-prompt"}",
    inputVariables: ${generateInputVariablesObject("javascript")},${
      mostRecentLabel ? `\n    promptReleaseLabel: "${mostRecentLabel}",` : ""
    }
    tags: ["production", "tutorial"],
    metadata: { userId: "abc123" }
});`
                }
                showRuntimeDetails={false}
                height="240px"
                editorOptions={{
                  fontSize: 12,
                  lineHeight: 18,
                  padding: { top: 12, bottom: 12 },
                  renderLineHighlight: "none",
                  readOnly: true,
                }}
              />
            </div>
            <p className="text-sm text-gray-600">
              Looking to set up caching and redundancy?{" "}
              <a
                href="https://docs.promptlayer.com/onboarding-guides/deployment-strategies#deployment-strategies"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800"
              >
                View deployment strategies
              </a>
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() =>
              window.open("https://docs.promptlayer.com/features/prompt-registry", "_blank")
            }
          >
            Go to Docs
          </Button>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeploymentGuideDialog;
